import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty, isEqual, omit } from 'lodash-es'
import { AxiosError } from 'axios'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import {
  createMltShipperCreateSchema,
  createMltShipperUpdateSchema,
  MLTShipperCreateForm,
  MLTShipperUpdateForm,
} from '@/services/shipper/schemas'
import {
  useCreateMLTShipper,
  useCreateSalesPlatform,
  useUpdateMLTShipper,
  useUpdateSalesPlatform,
  useDeleteSalesPlatform,
} from '@/services/shipper/useShipperQueries'
import { ShipperItem } from '@/services/shipper/types'
import { createExecuteOperations } from './shipperFormUtils'
import { useCompanyIdSpecialChars } from './useCompanyIdSpecialChars'

const defaultValues: MLTShipperCreateForm = {
  logisticsServiceType: 'MLT',
  shipperName: '',
  companyName: '',
  businessRegistrationNumber: '',
  ownerName: '',
  businessType: '',
  businessMajor: '',
  zipCode: '',
  address: '',
  phoneNumber: '',
  salesPlatforms: [{ type: '', name: '', companyId: '', usable: true }],
} as MLTShipperCreateForm

export function useMLTShipperCreateForm(onRefresh: () => Promise<void>, onClose: () => void) {
  const createMLTShipper = useCreateMLTShipper()
  const allowSpecialChars = useCompanyIdSpecialChars()
  const schema = useMemo(() => createMltShipperCreateSchema(allowSpecialChars), [allowSpecialChars])

  const form = useForm<MLTShipperCreateForm>({
    resolver: zodResolver(schema) as any,
    defaultValues,
    mode: 'onChange',
  })

  const isValid = schema.safeParse(form.watch()).success && isEmpty(form.formState.errors)

  const handleSubmit = async () => {
    try {
      const formData = form.getValues()

      const transformedFormData = {
        ...formData,
        salesPlatforms: formData.salesPlatforms.map((platform) => ({
          ...platform,
          type: platform.type === '29CM' || platform.type === 'MUSINSA' ? 'MANAGED' : platform.type,
        })),
      }

      await createMLTShipper.mutateAsync(transformedFormData as any)

      MToast.show('MLT 화주 추가가 완료되었습니다.')
      setTimeout(() => {
        form.reset()

        onRefresh()
        onClose()
      }, 100)
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, 'MLT 화주 추가에 실패했습니다.'))
    }
  }

  return {
    form,
    isValid,
    handleSubmit,
  }
}

export function useMLTShipperUpdateForm(shipperData: ShipperItem, onRefresh: () => Promise<void>, onClose: () => void) {
  const updateMLTShipper = useUpdateMLTShipper()
  const createSalesPlatform = useCreateSalesPlatform(shipperData.shipperCode)
  const updateSalesPlatform = useUpdateSalesPlatform(shipperData.shipperCode)
  const deleteSalesPlatform = useDeleteSalesPlatform(shipperData.shipperCode)
  const allowSpecialChars = useCompanyIdSpecialChars()
  const schema = useMemo(() => createMltShipperUpdateSchema(allowSpecialChars), [allowSpecialChars])

  const updateDefaultValues: MLTShipperUpdateForm = {
    logisticsServiceType: 'MLT',
    shipperCode: shipperData.shipperCode,
    shipperName: shipperData.shipperName,
    companyName: shipperData.companyName,
    businessRegistrationNumber: shipperData.businessRegistrationNumber,
    ownerName: shipperData.ownerName,
    businessType: shipperData.businessType || '',
    businessMajor: shipperData.businessMajor || '',
    zipCode: shipperData.zipCode || '',
    address: shipperData.address || '',
    phoneNumber: shipperData.phoneNumber || '',
    salesPlatforms: shipperData.salesPlatforms,
  }

  const form = useForm<MLTShipperUpdateForm>({
    resolver: zodResolver(schema) as any,
    defaultValues: updateDefaultValues,
    mode: 'onChange',
  })

  const isValid = schema.safeParse(form.watch()).success && isEmpty(form.formState.errors)

  const { executeOperations, showRetryConfirm } = createExecuteOperations(
    {
      createSalesPlatform,
      updateSalesPlatform,
      deleteSalesPlatform,
      updateShipper: updateMLTShipper,
    },
    shipperData,
    form,
    onRefresh,
    onClose,
  )

  const handleSubmit = async () => {
    try {
      const formData = form.getValues()
      const { salesPlatforms, shipperName, ...transformedFormData } = formData

      // salesPlatforms 비교 로직
      const originalPlatforms = shipperData.salesPlatforms || []
      const newPlatforms = salesPlatforms || []

      // 새로 추가된 플랫폼들 (masterCompanyId가 기존에 없던 것들)
      const platformsToCreate = newPlatforms.filter(
        (newPlatform) =>
          !newPlatform.masterCompanyId ||
          !originalPlatforms.some((originalPlatform) => originalPlatform.masterCompanyId === newPlatform.masterCompanyId),
      )

      // 수정된 플랫폼들 (masterCompanyId는 같지만 다른 필드가 변경된 것들)
      const platformsToUpdate = newPlatforms.filter((newPlatform) => {
        if (!newPlatform.masterCompanyId) return false

        const originalPlatform = originalPlatforms.find((original) => original.masterCompanyId === newPlatform.masterCompanyId)
        if (!originalPlatform) return false

        // 필드 비교 (masterCompanyId는 제외하고 비교)
        return !isEqual(omit(originalPlatform, 'masterCompanyId'), omit(newPlatform, 'masterCompanyId'))
      })

      // 삭제된 플랫폼들 (originalPlatforms에 있었지만 newPlatforms에는 없는 것들)
      const platformsToDelete = originalPlatforms.filter(
        (originalPlatform) =>
          originalPlatform.masterCompanyId && !newPlatforms.some((newPlatform) => newPlatform.masterCompanyId === originalPlatform.masterCompanyId),
      )

      const createOperations = platformsToCreate.map((platform, index) => ({
        type: 'create' as const,
        platform,
        index,
      }))

      const updateOperations = platformsToUpdate.map((platform, index) => ({
        type: 'update' as const,
        platform,
        index,
      }))

      const deleteOperations = platformsToDelete.map((platform, index) => ({
        type: 'delete' as const,
        platform,
        index,
      }))

      // 메인 shipper 업데이트 API
      const mainUpdateOperation = {
        type: 'main' as const,
      }

      const allOperations = [...createOperations, ...updateOperations, ...deleteOperations, mainUpdateOperation]
      const result = await executeOperations(allOperations)

      if (!result.success) {
        const failedOperations = result.operations.filter((_, index) => result.results[index].status === 'rejected')
        showRetryConfirm(failedOperations)
      }
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '일부 항목 저장에 실패했어요. 실패건을 확인하고 다시 시도해 주세요.'))
    }
  }

  return {
    form,
    isValid,
    handleSubmit,
  }
}
